import { PenIcon, TrashIcon } from "@xola/icons";
import { Label } from "@xola/ui-kit";
import clsx from "clsx";
import { getPrimaryUnitCatalog } from "common";
import { FormikErrors } from "formik";
import React, { useState } from "react";
import { CatalogItem, PartnerDemographicPrice, PartnerDemographicTerm, Product, TermsTemplate, TermsType } from "types";
import { useProduct } from "../../../hooks/resources/useProduct";
import { getInitialPartnerDemographicPrices } from "../../../models/TermsTemplatePurchaseRule";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { Loader } from "../../Loader";
import { DemographicTermTable } from "./DemographicTermTable";
import { EditDemographicTermModal } from "./EditDemographicTermModal";

interface AdvancedTermsFormProps {
    values: TermsTemplate;
    errors?: FormikErrors<TermsTemplate>;
    onChange: (value: PartnerDemographicTerm[]) => void;
    onDeleteTerm: (Id: ID) => void;
}

export const AdvancedTermsForm = ({ values, errors, onChange, onDeleteTerm }: AdvancedTermsFormProps) => {
    let productIds = values.products.selected;
    if (values.products.all && values.products.data?.length && values.termsType === TermsType.ADVANCED) {
        productIds = values.products.data ?? [];
    }

    return (
        <>
            <Label className={clsx("whitespace-nowrap py-2", errors?.partnerDemographicTerms && "!text-danger")}>
                Purchasable Unit Terms {errors?.partnerDemographicTerms}
            </Label>
            <div>
                {productIds.length > 0 ? (
                    productIds.map((productId: ID) => {
                        return (
                            <DemographicTermsItem
                                key={productId}
                                productId={productId}
                                values={values}
                                onChange={onChange}
                                onDeleteTerm={onDeleteTerm}
                            />
                        );
                    })
                ) : (
                    <h5>Choose Product to apply Terms</h5>
                )}
            </div>
        </>
    );
};

interface DemographicTermsItemProps {
    productId: ID;
    values: TermsTemplate;
    onChange: (value: PartnerDemographicTerm[]) => void;
    onDeleteTerm: (Id: ID) => void;
}

export const DemographicTermsItem = ({ productId, values, onChange, onDeleteTerm }: DemographicTermsItemProps) => {
    const [isDemographicPriceModalOpen, setIsDemographicPriceModalOpen] = useState(false);
    let partnerDemographicPrices: PartnerDemographicPrice[] = [];

    const { product, isLoadingProduct } = useProduct(productId);

    if (isLoadingProduct) {
        return <Loader />;
    }

    // Current Product Demographic Term values
    const partnerDemographicTerm = getPartnerDemographicTerm(values.partnerDemographicTerms, productId);

    const newPartnerDemographicTerm: PartnerDemographicTerm = {
        product: { id: productId, name: product?.name },
        partnerDemographicPrices: [],
        ...partnerDemographicTerm?.partnerDemographicPrices,
    };

    const updatePartnerDemographicTerms = (partnerDemographicPrices: PartnerDemographicPrice[]) => {
        const filteredPartnerDemographicTerms = values.partnerDemographicTerms?.filter(
            (partnerDemographicTerm: PartnerDemographicTerm) => partnerDemographicTerm.product.id !== productId,
        );
        newPartnerDemographicTerm.partnerDemographicPrices = partnerDemographicPrices;
        onChange([...filteredPartnerDemographicTerms, newPartnerDemographicTerm]);
    };

    if (product) {
        partnerDemographicPrices = getPartnerDemographicPrices(product, partnerDemographicTerm);
    }

    const handleSubmit = (partnerDemographicPrices: PartnerDemographicPrice[]) => {
        updatePartnerDemographicTerms(partnerDemographicPrices);
        setIsDemographicPriceModalOpen(false);
    };

    return product ? (
        <>
            <div className="my-2 flex items-center justify-between">
                <NoTranslate as="div" className="flex items-center">
                    <ProductImage
                        product={{ id: product.id, name: product.name, object: product.object }}
                        size="small"
                    />
                    <Label className="no-translate">{product.name}</Label>
                </NoTranslate>

                <div className="space-x-2">
                    <InlineConfirmDeletePopover confirmButtonText="Confirm" onConfirm={() => onDeleteTerm(productId)}>
                        <SecondaryButton size="small">
                            <div className="space-x-1">
                                <TrashIcon /> <span>Delete</span>
                            </div>
                        </SecondaryButton>
                    </InlineConfirmDeletePopover>
                    <SecondaryButton
                        icon={<PenIcon />}
                        size="small"
                        onClick={() => setIsDemographicPriceModalOpen(true)}
                    >
                        Edit
                    </SecondaryButton>
                </div>
            </div>

            <DemographicTermTable partnerDemographicPrices={partnerDemographicPrices} />

            <EditDemographicTermModal
                isOpen={isDemographicPriceModalOpen}
                partnerDemographicPrices={partnerDemographicPrices}
                product={product}
                onClose={() => setIsDemographicPriceModalOpen(false)}
                onSubmit={handleSubmit}
            />
        </>
    ) : null;
};

export const getPartnerDemographicTerm = (partnerDemographicTerms: PartnerDemographicTerm[], productId: ID) => {
    return partnerDemographicTerms?.find(
        (partnerDemographicTerm: PartnerDemographicTerm) => partnerDemographicTerm.product.id === productId,
    );
};

export const getPartnerDemographicPrices = (
    product: Product,
    partnerDemographicTerm?: PartnerDemographicTerm,
): PartnerDemographicPrice[] => {
    const partnerDemographicPrices: PartnerDemographicPrice[] = [];

    const unitCatalogItems: CatalogItem[] = getPrimaryUnitCatalog(product);
    if (partnerDemographicTerm?.partnerDemographicPrices?.length) {
        for (const catalogItem of unitCatalogItems) {
            const agentDemographicPrice = partnerDemographicTerm.partnerDemographicPrices.find(
                (agentDemographicPrice: PartnerDemographicPrice) =>
                    agentDemographicPrice.template.id === catalogItem.template?.id,
            );
            partnerDemographicPrices.push(getInitialPartnerDemographicPrices(catalogItem, agentDemographicPrice));
        }
    } else {
        // initialise demographic Prices form value and update, if no partnerDemographicPrices exists
        for (const catalogItem of unitCatalogItems) {
            partnerDemographicPrices.push(getInitialPartnerDemographicPrices(catalogItem));
        }
    }

    return partnerDemographicPrices;
};
